import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Button from '../shared/components/Button';
import { memberApi } from '../api';
import { auth } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const MemberLogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { memberLogin } = useAuth();

  const registrationStatus = searchParams.get('registration');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError('');
      setLoading(true);
      await memberLogin(email, password);
      
      // Get the current user's token
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Failed to get current user after login');
      }
      const token = await user.getIdToken();
      
      // Check if user has a pending registration
      const facilityId = localStorage.getItem('facilityId');
      const businessId = localStorage.getItem('businessId');
      if (facilityId && businessId) {
        try {
          // First check if the facility is PPU
          const facilityRef = doc(db, 'businesses', businessId, 'facilities', facilityId);
          const facilityDoc = await getDoc(facilityRef);
          
          if (!facilityDoc.exists) {
            throw new Error('Facility not found');
          }
          
          const facilityData = facilityDoc.data();
          const isPPU = facilityData?.isPPU;
          
          // Check if user is imported and get their status
          const memberRef = doc(db, 'businesses', businessId, 'facilities', facilityId, 'users', user.uid);
          const memberDoc = await getDoc(memberRef);
          const memberData = memberDoc.exists() ? memberDoc.data() : null;
          const isImported = memberData?.imported;
          
          if (!isPPU) {
            // Only check subscription for non-PPU facilities
            const subscription = await memberApi.getSubscription(user.uid, token, businessId, facilityId);
            
            // Check if subscription is pending or doesn't exist, or if user is imported and needs setup
            if (!subscription || subscription.status === 'pending' || !subscription.paymentMethod || 
                (isImported && memberData?.subscriptionStatus === 'pending')) {
              // Store the email in session storage for the registration flow
              sessionStorage.setItem('pending_registration_email', email);
              // Redirect to plan selection if subscription is pending or incomplete
              navigate(`/member/plans?facilityId=${facilityId}`);
              return;
            }
          }
        } catch (err) {
          console.error('Error checking subscription status:', err);
          // If we can't verify subscription status, redirect to plan selection to be safe
          navigate(`/member/plans?facilityId=${facilityId}`);
          return;
        }
      }
      
      // If no pending registration or error checking status, proceed to dashboard
      navigate('/member/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign in');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <nav className="border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Link to="/" className="text-xl font-medium">
                Courted.ai
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex flex-col justify-center py-24 sm:px-6 lg:px-8">
        {registrationStatus === 'complete' && (
          <div className="max-w-md mx-auto w-full mb-4">
            <div className="bg-green-900/50 border border-green-500/50 text-green-200 px-4 py-3 rounded-xl" role="alert">
              <span className="block sm:inline">Registration completed successfully! Please sign in to continue.</span>
            </div>
          </div>
        )}

        {(registrationStatus === 'failed' || registrationStatus === 'incomplete') && (
          <div className="max-w-md mx-auto w-full mb-4">
            <div className="bg-red-900/50 border border-red-500/50 text-red-200 px-4 py-3 rounded-xl" role="alert">
              <span className="block sm:inline">
                {registrationStatus === 'failed' 
                  ? 'Registration could not be completed. Please try again or contact support.'
                  : 'Registration was not completed. Please start the registration process again.'}
              </span>
            </div>
          </div>
        )}

        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h2 className="text-center text-4xl font-medium tracking-tight mb-2">
            Welcome Back
          </h2>
          <p className="text-center text-gray-400 text-lg">
            Sign in to your member account
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-gray-900 py-8 px-4 shadow sm:rounded-2xl sm:px-10 border border-gray-800">
            {error && (
              <div className="bg-red-900/50 border border-red-500/50 text-red-200 px-4 py-3 rounded-xl mb-6" role="alert">
                <span className="block sm:inline">{error}</span>
              </div>
            )}
            
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="appearance-none block w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white sm:text-sm"
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-300">
                  Password
                </label>
                <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="appearance-none block w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white sm:text-sm"
                    placeholder="Enter your password"
                  />
                </div>
              </div>

              <div>
                <Button
                  type="submit"
                  disabled={loading}
                  variant="dark"
                  className="w-full flex justify-center py-3 text-sm"
                >
                  {loading ? 'Signing in...' : 'Sign in →'}
                </Button>
              </div>
            </form>

            <div className="mt-6">
              <div className="relative">
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 text-gray-400">
                    Don't have an account?{' '}
                    <Link to="/member/register" className="font-medium text-blue-500 hover:text-blue-400">
                      Register now →
                    </Link>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberLogin; 